import { api } from './api';

// Types
export interface GlobalSchedule {
  id: string;
  globalLeagueDivisionId: string;
  autoSyncEnabled: boolean;
  syncFrequency: 'hourly' | 'daily' | 'weekly';
  syncTimeOfDay?: string;
  syncPriority?: number;
  nextSyncAt?: string;
  lastSyncAt?: string;
  lastSyncStatus?: 'success' | 'error' | 'in_progress';
  lastSyncError?: string;
  syncLockId?: string;
  syncLockExpiresAt?: string;
  matchCount?: number;
  teamCount?: number;
  subscribedOrgsCount?: number;
  // League defaults (cascaded to org leagues)
  defaultTimezone?: string;
  winPoints?: number;
  drawPoints?: number;
  lossPoints?: number;
  createdAt: string;
  updatedAt: string;
}

export interface GlobalLeagueDivision {
  id: string;
  globalLeagueId: string;
  externalId: string;
  name: string;
  sourceName?: string; // Original name from discovery, used as fallback
  scheduleUrl: string;
  ageGroup?: string;
  gender?: string;
  division?: string;
  subLeague?: string; // e.g., "ecnl", "ecnlrl"
  flight?: string; // e.g., "Blue", "Yellow" (for ECNL flights)
  playoffsEnabled?: boolean;
  playoffSpots?: number;
  lastSyncAt?: string;
  lastSyncStatus?: string;
  schedule?: GlobalSchedule;
  subscribedOrgsCount?: number; // Number of orgs that imported this division
  createdAt: string;
  updatedAt: string;
}

export interface GlobalScheduleLockStatus {
  isLocked: boolean;
  lockedSince?: string;
  lockExpiresAt?: string;
}

export interface UpdateGlobalScheduleSettingsInput {
  autoSyncEnabled?: boolean;
  syncFrequency?: 'hourly' | 'daily' | 'weekly';
  syncTimeOfDay?: string;
  syncPriority?: number;
  defaultTimezone?: string;
  winPoints?: number;
  drawPoints?: number;
  lossPoints?: number;
}

export interface GlobalLeague {
  id: string;
  name: string;
  website: string;
  logoUrl?: string;
  logoSmallUrl?: string;
  logoMediumUrl?: string;
  logoSourceUrl?: string;
  logoPath?: string;
  logoUpdatedAt?: string;
  platformType: 'GOTSPORT' | 'SINCSPORTS' | 'MODULAR11' | 'TGS';
  playoffsEnabled: boolean;
  playoffSpots?: number;
  winPoints: number;
  drawPoints: number;
  lossPoints: number;
  defaultTimezone?: string; // IANA timezone (e.g., "America/New_York")
  lastDiscoveryAt?: string;
  discoveryStatus: 'pending' | 'in_progress' | 'success' | 'error';
  discoveryError?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  divisions: GlobalLeagueDivision[];
  _count?: {
    divisions: number;
  };
}

export interface CreateGlobalLeagueInput {
  name: string;
  website: string;
  logoUrl?: string;
  logoSmallUrl?: string;
  logoMediumUrl?: string;
  playoffsEnabled?: boolean;
  playoffSpots?: number;
}

export interface UpdateGlobalLeagueInput {
  name?: string;
  logoUrl?: string;
  logoSmallUrl?: string;
  logoMediumUrl?: string;
  playoffsEnabled?: boolean;
  playoffSpots?: number;
  winPoints?: number;
  drawPoints?: number;
  lossPoints?: number;
}

export interface DiscoveryProgress {
  type: 'progress' | 'result' | 'error';
  phase?: 'connecting' | 'fetching_page' | 'extracting' | 'saving' | 'complete' | 'error';
  percent?: number;
  message?: string;
  detail?: string;
  divisionsFound?: number;
  league?: GlobalLeague;
  error?: string;
}

export interface ImportFromCatalogInput {
  divisionId: string;
  name?: string; // Custom name (overrides auto-generated name)
  division?: string; // Division name (e.g., "U14 NPL 2")
  logoUrl?: string; // Custom logo URL (overrides catalog logo)
  playoffsEnabled?: boolean;
  playoffSpots?: number;
  defaultTimezone?: string;
  teams?: string[]; // Pre-fetched team names to create immediately
}

// Super Admin Service
export const globalLeagueAdminService = {
  // List all global leagues
  async listGlobalLeagues(search?: string): Promise<GlobalLeague[]> {
    const params = search ? { search } : {};
    const response = await api.get('/super-admin/global-leagues', { params });
    return response.data;
  },

  // Get a single global league with divisions
  async getGlobalLeague(id: string): Promise<GlobalLeague> {
    const response = await api.get(`/super-admin/global-leagues/${id}`);
    return response.data;
  },

  // Create a new global league
  async createGlobalLeague(input: CreateGlobalLeagueInput): Promise<GlobalLeague> {
    const response = await api.post('/super-admin/global-leagues', input);
    return response.data;
  },

  // Create global league with background discovery
  // Discovery runs in the background; progress is delivered via the catalog SSE stream.
  async createGlobalLeagueWithProgress(input: CreateGlobalLeagueInput): Promise<GlobalLeague> {
    const response = await api.post('/super-admin/global-leagues/discover', input);
    return response.data;
  },

  // Download and set logo for a global league
  async downloadGlobalLeagueLogo(id: string, url: string): Promise<GlobalLeague> {
    const response = await api.post(`/super-admin/global-leagues/${id}/download-logo`, { url });
    return response.data;
  },

  // Update a global league
  async updateGlobalLeague(id: string, input: UpdateGlobalLeagueInput): Promise<GlobalLeague> {
    const response = await api.put(`/super-admin/global-leagues/${id}`, input);
    return response.data;
  },

  // Delete a global league
  async deleteGlobalLeague(id: string): Promise<void> {
    await api.delete(`/super-admin/global-leagues/${id}`);
  },

  // Refresh divisions with SSE progress
  async refreshDivisionsWithProgress(
    id: string,
    callbacks: {
      onProgress: (progress: DiscoveryProgress) => void;
      onResult: (league: GlobalLeague) => void;
      onError: (error: string) => void;
    }
  ): Promise<void> {
    try {
      // Fire-and-forget POST: discovery runs in the background on the server.
      // Progress/done/error are delivered via the catalog SSE stream.
      await api.post(`/super-admin/global-leagues/${id}/refresh`);
    } catch (error: any) {
      callbacks.onError(error?.response?.data?.error || error.message || 'Failed to start discovery');
    }
  },

  // Get global league with schedules (full details)
  async getGlobalLeagueWithSchedules(id: string): Promise<GlobalLeague> {
    const response = await api.get(`/super-admin/global-leagues/${id}?includeSchedules=true`);
    return response.data;
  },

  // Get lock status for a global schedule
  async getGlobalScheduleLockStatus(scheduleId: string): Promise<GlobalScheduleLockStatus> {
    const response = await api.get(`/super-admin/global-schedules/${scheduleId}/lock-status`);
    return response.data;
  },

  // Update global schedule settings
  async updateGlobalScheduleSettings(
    scheduleId: string,
    settings: UpdateGlobalScheduleSettingsInput
  ): Promise<void> {
    await api.put(`/super-admin/global-schedules/${scheduleId}`, settings);
  },

  // Trigger manual sync for a global schedule
  async triggerGlobalSync(scheduleId: string): Promise<{ jobId: string }> {
    const response = await api.post(`/super-admin/global-schedules/${scheduleId}/sync`);
    return response.data.data;
  },

  // Get team cache stats across all leagues
  async getTeamCacheStats(): Promise<{
    overall: { total: number; cached: number; empty: number };
    leagues: Array<{ id: string; name: string; total: number; cached: number; empty: number }>;
  }> {
    const response = await api.get('/super-admin/global-leagues/cache-stats');
    return response.data;
  },

  // Retry team caching for empty divisions in a league
  async retryTeamCache(id: string): Promise<void> {
    await api.post(`/super-admin/global-leagues/${id}/retry-team-cache`);
  },

  // Update a global league division (cascades to all linked org leagues)
  async updateGlobalLeagueDivision(
    divisionId: string,
    updates: { name?: string; playoffsEnabled?: boolean; playoffSpots?: number }
  ): Promise<void> {
    await api.put(`/super-admin/global-leagues/divisions/${divisionId}`, updates);
  },

};

// User Catalog Service
export const globalLeagueCatalogService = {
  // Get available global leagues (catalog)
  async getCatalog(search?: string): Promise<GlobalLeague[]> {
    const params = search ? { search } : {};
    const response = await api.get('/leagues/catalog', { params });
    return response.data;
  },

  // Get divisions for a catalog league
  async getDivisions(
    globalLeagueId: string,
    filters?: { ageGroup?: string; gender?: string; search?: string; subLeague?: string }
  ): Promise<GlobalLeagueDivision[]> {
    const response = await api.get(`/leagues/catalog/${globalLeagueId}/divisions`, {
      params: filters,
    });
    return response.data;
  },

  // Import a division from the catalog
  async importFromCatalog(input: ImportFromCatalogInput): Promise<any> {
    const response = await api.post('/leagues/catalog/import', input);
    return response.data;
  },

  // Preview teams for a division
  async previewTeams(divisionId: string): Promise<{ teams: string[]; error?: string }> {
    const response = await api.get(`/leagues/catalog/divisions/${divisionId}/teams`);
    return response.data;
  },

  // Warm team name cache for a league (fire-and-forget, called on league select)
  async warmTeamCache(leagueId: string): Promise<void> {
    await api.post(`/leagues/catalog/${leagueId}/warm-team-cache`);
  },

  // Search teams by name across all divisions of a league
  async searchTeams(
    leagueId: string,
    query: string
  ): Promise<{
    divisions: Array<{ division: GlobalLeagueDivision; matchingTeams: string[] }>;
    caching: boolean;
    totalDivisions: number;
    searchedDivisions: number;
  }> {
    const response = await api.get(`/leagues/catalog/${leagueId}/search-teams`, {
      params: { q: query },
    });
    return response.data;
  },

  // Get division IDs already imported into the current organization
  async getImportedDivisions(): Promise<string[]> {
    const response = await api.get('/leagues/catalog/imported-divisions');
    return response.data.divisionIds || [];
  },
};
